/**
 * Keys for the stale-while-revalidate cache (`@hooks/useCachedFetch`) on Reflector V2
 * screens. They live here, not in each page, because more than one screen reads or
 * prunes the same entry: a cluster's propagation rules are shown by the cluster and by
 * its member reflectors, and dissolving a cluster prunes the cluster list.
 *
 * Every key carries the tenant the request is made with, so one customer's answer
 * never paints under another customer's lens.
 */
type Id = string | number;

export const reflectorListCacheKey = (tenantId: number) =>
  `reflector-v2/reflectors?tenantId=${tenantId}`;

export const reflectorDetailsCacheKey = (reflectorId: Id, tenantId: number) =>
  `reflector-v2/reflectors/${reflectorId}?tenantId=${tenantId}`;

export const reflectorIamCacheKey = (reflectorId: Id, tenantId: number) =>
  `reflector-v2/reflectors/${reflectorId}/iam-permissions?tenantId=${tenantId}`;

export const reflectorBgpConfigCacheKey = (reflectorId: Id, tenantId: number) =>
  `reflector-v2/reflectors/${reflectorId}/bgp-config?tenantId=${tenantId}`;

export const reflectorCloudPropagationCacheKey = (reflectorId: Id, tenantId: number) =>
  `reflector-v2/reflectors/${reflectorId}/cloud-propagation?tenantId=${tenantId}`;

export const clusterListCacheKey = (tenantId: number) =>
  `reflector-v2/clusters?tenantId=${tenantId}`;

export const clusterDetailsCacheKey = (clusterId: Id, tenantId: number) =>
  `reflector-v2/clusters/${clusterId}?tenantId=${tenantId}`;

export const clusterIamCacheKey = (clusterId: Id, tenantId: number) =>
  `reflector-v2/clusters/${clusterId}/iam-permissions?tenantId=${tenantId}`;

export const clusterBgpConfigCacheKey = (clusterId: Id, tenantId: number) =>
  `reflector-v2/clusters/${clusterId}/bgp-config?tenantId=${tenantId}`;

/** Holds the raw `GET .../cloud-propagation` body, whichever screen fetched it. */
export const clusterCloudPropagationCacheKey = (clusterId: Id, tenantId: number) =>
  `reflector-v2/clusters/${clusterId}/cloud-propagation?tenantId=${tenantId}`;

export const overviewSectionCacheKey = (
  tenantId: number,
  type: string,
  limit: number,
  page: number
) => `reflector-v2/overview?tenantId=${tenantId}&type=${type}&limit=${limit}&page=${page}`;
